export interface TranscriptionSegment {
  start: number;
  end: number;
  text: string;
}

export async function transcribeAudio(
  audioBlob: Blob,
  onProgress?: (progress: number, stage: string) => void
): Promise<TranscriptionSegment[]> {
  if (onProgress) onProgress(50, 'transcribing');
  
  try {
    console.log(`Sending audio to API for transcription (${(audioBlob.size / 1024 / 1024).toFixed(2)}MB)`);
    
    // Send audio to Cloudflare Workers AI via API
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.wav');
    
    const API_URL = 'https://clipgov-api.wananchi-media.workers.dev/api/transcribe';
    
    const response = await fetch(API_URL, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `API error: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (onProgress) onProgress(100, 'transcribing');
    
    console.log(`Transcription complete: ${result.text?.substring(0, 100)}...`);
    
    // Convert API response to segments
    // For now, return full text as single segment
    // TODO: Parse timestamps if API returns them
    const segments: TranscriptionSegment[] = [{
      start: 0,
      end: 0, // Will be set by caller
      text: result.text || ''
    }];
    
    return segments;
    
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error('Transcription error:', error);
    throw new Error(`Transcription failed: ${msg}`);
  }
}

export async function transcribeVideo(
  videoFile: File,
  onProgress?: (progress: number, stage: string) => void
): Promise<TranscriptionSegment[]> {
  // Check if file is audio or video
  const isAudio = videoFile.type.startsWith('audio/');
  
  let audioBlob: Blob;
  
  if (isAudio) {
    // Audio file - send directly (no extraction needed)
    if (onProgress) onProgress(30, 'extracting');
    console.log(`Audio file detected: ${videoFile.name} - sending directly`);
    audioBlob = videoFile;
  } else {
    // Video file - extract audio first (local - fast with Web Audio API)
    if (onProgress) onProgress(0, 'extracting');
    
    const { extractAudio } = await import('./ffmpeg');
    audioBlob = await extractAudio(videoFile, (ffmpegProgress) => {
      if (onProgress) onProgress(ffmpegProgress * 0.3, 'extracting');
    });
  }
  
  if (onProgress) onProgress(30, 'decoding');
  
  // Transcribe audio via API (cloud - fast with Workers AI)
  return transcribeAudio(audioBlob, onProgress);
}
